import React, { useEffect, useMemo, useState } from "react";
import { clock, dayKey, groupByDay, longDay, zoneCity } from "./format";
import { ZonePicker } from "./parts";

/*
 * THE DESKTOP BOOKING PAGES, drawn to "Remotion_Booking_-_Desktop.pptx"
 * (Claude Design, 2026-09-30): days on the left, the chosen day's times on the
 * right by morning, afternoon and evening, and the form opening right under
 * the time that was clicked. A quiet side column says who they're meeting and
 * what happens on the call.
 *
 * Phones keep the v4 layout untouched; this switches on at WIDE and up.
 */

const WIDE = "(min-width: 1000px)";

export function useWide() {
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(WIDE).matches);
  useEffect(() => {
    const media = window.matchMedia(WIDE);
    const onChange = () => setWide(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return wide;
}

/** The thin black bar across the top of the screens that have no big header. */
export function TopBar({ right, children }) {
  return (
    <div className="bkd-bar">
      <div className="bkd-bar__left">
        <Logo />
        {children}
      </div>
      {right ? <span className="bk-mono bkd-bar__right">{right}</span> : null}
    </div>
  );
}

export function Logo() {
  return (
    <img
      className="bkd-logo"
      src="/remotion-logo.png"
      srcSet="/remotion-logo.png 1x, /remotion-logo@2x.png 2x"
      alt="Remotion"
    />
  );
}

/** The big dark header: logo row, optional kicker, headline. */
export function DeskHeader({ right, kicker, children }) {
  return (
    <header className="bkd-header">
      <div className="bkd-header__row">
        <Logo />
        {right ? <span className="bk-mono bkd-bar__right">{right}</span> : null}
      </div>
      {kicker ? <span className="bk-mono bk-kicker bkd-kicker">{kicker}</span> : null}
      <h1 className="bkd-headline">{children}</h1>
    </header>
  );
}

function hourIn(iso, zone) {
  return Number(
    new Intl.DateTimeFormat("en-US", { timeZone: zone, hour: "2-digit", hourCycle: "h23" }).format(new Date(iso))
  );
}

const PARTS = [
  ["Morning", (h) => h < 12],
  ["Afternoon", (h) => h >= 12 && h < 17],
  ["Evening", (h) => h >= 17],
];

function dayLabel(key) {
  const date = new Date(`${key}T12:00:00Z`);
  const f = (o) => new Intl.DateTimeFormat("en-US", { timeZone: "UTC", ...o }).format(date);
  return { wd: f({ weekday: "short" }), md: f({ month: "short", day: "numeric" }), long: f({ weekday: "long", month: "long", day: "numeric" }) };
}

/*
 * The picker card. `panel(slot)` renders the box that opens under the clicked
 * time's group (the booking form, or the move button). `taken` is a start that
 * was just lost, shown struck through where it was.
 */
export function DeskPicker({ label = "When", zone, onZone, slots, days, day, onDay, selected, onSelect, taken = null, panel, minutes = 30, where = "Google Meet", fixedZone = false }) {
  const byDay = useMemo(() => groupByDay(slots, zone), [slots, zone]);
  const allDays = useMemo(() => {
    const keys = new Set([...(days || []), ...byDay.keys()]);
    return [...keys].sort();
  }, [days, byDay]);
  const firstOpen = allDays.find((k) => (byDay.get(k) || []).length) || allDays[0];
  const active = day && allDays.includes(day) ? day : firstOpen;
  const daySlots = (byDay.get(active) || []).slice();
  if (taken && dayKey(taken, zone) === active && !daySlots.some((s) => s.start === taken)) {
    daySlots.push({ start: taken, taken: true });
    daySlots.sort((a, b) => new Date(a.start) - new Date(b.start));
  }
  const selectedSlot = selected ? daySlots.find((s) => s.start === selected) : null;
  const panelPart = selectedSlot ? PARTS.findIndex(([, test]) => test(hourIn(selectedSlot.start, zone))) : -1;
  const open = (byDay.get(active) || []).length;

  return (
    <section className="bkd-picker">
      <div className="bkd-picker__top">
        <span className="bkd-picker__zone">
          <span className="bk-mono">{label}</span>
          {fixedZone ? <span className="bk-muted">{zoneCity(zone)} time</span> : <ZonePicker zone={zone} onChange={onZone} />}
        </span>
        <span className="bk-muted">{minutes} min · {where}</span>
      </div>
      <div className="bkd-picker__body">
        <div className="bkd-days" role="group" aria-label="Day">
          {allDays.map((key) => {
            const n = (byDay.get(key) || []).length;
            const l = dayLabel(key);
            return (
              <button
                key={key}
                type="button"
                className="bkd-day"
                disabled={!n}
                aria-pressed={key === active}
                onClick={() => onDay(key)}
              >
                <b>{l.wd}</b>
                <span>{l.md}</span>
                <small className="bk-mono">{n ? `${n} time${n === 1 ? "" : "s"}` : "Full"}</small>
              </button>
            );
          })}
        </div>
        <div className="bkd-times">
          <div className="bkd-times__head">
            <h2>{active ? dayLabel(active).long : ""}</h2>
            <span className="bk-mono bk-muted">
              {open} open time{open === 1 ? "" : "s"}
            </span>
          </div>
          {PARTS.map(([name, test], i) => {
            const group = daySlots.filter((s) => test(hourIn(s.start, zone)));
            if (!group.length) return null;
            return (
              <React.Fragment key={name}>
                <span className="bk-mono bkd-part">{name}</span>
                <div className="bkd-grid">
                  {group.map((slot) => (
                    <button
                      key={slot.start}
                      type="button"
                      className="bk-time bkd-time"
                      disabled={!!slot.taken}
                      aria-pressed={selected === slot.start}
                      onClick={() => onSelect(slot)}
                    >
                      {clock(slot.start, zone)}
                    </button>
                  ))}
                </div>
                {i === panelPart && panel ? <div className="bkd-panel">{panel(selectedSlot)}</div> : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** "TUE, OCT 6 · 8:00-8:30 · LONDON TIME" over the panel's contents. */
export function slotLine(slot, zone) {
  return `${longDay(slot.start, zone)} · ${clock(slot.start, zone)}–${clock(slot.end, zone)} · ${zoneCity(zone)} time`;
}

/** The side column: who they're meeting, and what happens on the call. */
export function MeetingSide({ host, title, photo, minutes = 30, agendas, where = null }) {
  return (
    <aside className="bkd-side">
      <span className="bk-mono">You're meeting</span>
      <div className="bkd-host">
        {photo ? <img className="bkd-avatar" src={photo} alt={host} /> : <span className="bkd-avatar bkd-avatar--initial">{String(host || "G").charAt(0)}</span>}
        <div>
          <b>{host}</b>
          {title ? <p>{title}</p> : null}
        </div>
      </div>
      <hr className="bkd-rule" />
      <span className="bk-mono">What happens on this call</span>
      <p className="bkd-side__lede">{where ? `${minutes} minutes in person, ${where}.` : `${minutes} minutes on Google Meet, just you and me.`}</p>
      {agendas && agendas.length ? (
        <>
          <p className="bkd-side__lede">Once it's booked, you choose what we cover:</p>
          <ul className="bkd-side__agendas">
            {agendas.map((a) => (
              <li key={a.key}>{a.label}</li>
            ))}
          </ul>
        </>
      ) : null}
      <p className="bk-muted">Need to move it later? Use the link in your invite.</p>
    </aside>
  );
}

/** "▶ VIDEO · 2 MIN" or "3 MIN READ". */
export function readKind(item) {
  if (item.video) return `▶ Video${item.minutes ? ` · ${item.minutes} min` : ""}`;
  return item.minutes ? `${item.minutes} min read` : "Read";
}
